import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Mail, Bell, Save, RotateCcw, Info, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_INFO = {
  confirmation: {
    name: "Email de confirmation",
    description: "Envoyé automatiquement lors de l'inscription à un événement",
    icon: Mail,
    color: "bg-green-100 text-green-700"
  },
  reminder: {
    name: "Email de rappel",
    description: "Envoyé automatiquement 2 jours avant l'événement",
    icon: Bell,
    color: "bg-blue-100 text-blue-700"
  }
};

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState({});
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('confirmation');
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchVariables();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/email-templates`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Erreur lors du chargement');
      const data = await response.json();
      
      const templatesMap = {};
      data.forEach(t => {
        templatesMap[t.template_type] = t;
      });
      setTemplates(templatesMap);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchVariables = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email-templates/variables`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVariables(data.variables || []);
      }
    } catch (error) {
      console.error('Failed to fetch variables:', error);
    }
  };

  const handleChange = (templateType, field, value) => {
    setTemplates(prev => ({
      ...prev,
      [templateType]: {
        ...prev[templateType],
        [field]: value
      }
    }));
  };

  const handleSave = async (templateType) => {
    const template = templates[templateType];
    if (!template) return;

    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email-templates/${templateType}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          subject: template.subject,
          title: template.title,
          body: template.body,
          footer: template.footer
        })
      });

      if (!response.ok) throw new Error('Erreur lors de la sauvegarde');
      
      toast.success('Template sauvegardé avec succès');
      fetchTemplates();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (templateType) => {
    if (!confirm('Êtes-vous sûr de vouloir réinitialiser ce template aux valeurs par défaut ?')) {
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email-templates/${templateType}/reset`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Erreur lors de la réinitialisation');
      
      toast.success('Template réinitialisé');
      fetchTemplates();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (variable) => {
    const textarea = document.activeElement;
    if (textarea && textarea.tagName === 'TEXTAREA') {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = textarea.value;
      const newValue = currentValue.substring(0, start) + `{${variable}}` + currentValue.substring(end);
      
      // Find which field is being edited
      const template = templates[activeTab];
      if (template) {
        if (textarea.name === 'body') {
          handleChange(activeTab, 'body', newValue);
        } else if (textarea.name === 'footer') {
          handleChange(activeTab, 'footer', newValue);
        }
      }
    } else {
      // If no textarea is focused, copy to clipboard
      navigator.clipboard.writeText(`{${variable}}`);
      toast.info(`Variable {${variable}} copiée !`);
    }
  };

  const renderPreview = (template) => {
    if (!template) return null;

    const sampleData = {
      nom_participant: "Jean Dupont",
      nom_evenement: "Soirée jeux de société",
      date_evenement: "Samedi 15 février 2025 à 14h00",
      lieu: "Plume et Pion",
      code_confirmation: "ABC12345",
      nom_boutique: "Plume et Pion"
    };

    let previewBody = template.body;
    let previewSubject = template.subject;
    let previewTitle = template.title;
    let previewFooter = template.footer;

    Object.entries(sampleData).forEach(([key, value]) => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      previewBody = previewBody.replace(regex, value);
      previewSubject = previewSubject.replace(regex, value);
      previewTitle = previewTitle.replace(regex, value);
      previewFooter = previewFooter.replace(regex, value);
    });

    return (
      <div className="border-2 border-[#E5E0D8] rounded-xl overflow-hidden bg-white">
        {/* Email header */}
        <div className="bg-gradient-to-r from-[#db3b17] to-[#ff6b47] text-white p-6 text-center">
          <p className="text-4xl mb-2">🎲</p>
          <h2 className="text-2xl font-bold">{previewTitle}</h2>
        </div>
        
        {/* Email body */}
        <div className="p-6 whitespace-pre-wrap leading-relaxed">
          {previewBody}
        </div>
        
        {/* Email footer */}
        <div className="bg-gray-100 p-4 text-center text-gray-600 text-sm">
          <p>Plume et Pion - Votre librairie ludique près de Tours</p>
          <p className="text-xs mt-1">{previewFooter}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout title="Modèles d'emails">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#db3b17] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Modèles d'emails">
      <div className="max-w-5xl">
        {/* Header */}
        <div className="mb-8">
          <p className="text-[#645E55]">
            Personnalisez les emails envoyés automatiquement à vos participants.
          </p>
        </div>

        {/* Variables helper */}
        <Card className="border-2 border-[#E5E0D8] rounded-2xl mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base text-[#2D2A26] flex items-center gap-2">
              <Info className="w-4 h-4 text-[#48b4ff]" />
              Variables disponibles
            </CardTitle>
            <CardDescription>
              Cliquez sur une variable pour l'insérer dans votre texte
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {variables.map((v) => (
                <button
                  key={v.key}
                  onClick={() => insertVariable(v.key)}
                  className="inline-flex items-center px-3 py-1.5 bg-[#FFF8E1] hover:bg-[#ffd451] text-[#2D2A26] rounded-lg text-sm font-mono transition-colors"
                  title={`${v.description} (ex: ${v.example})`}
                  data-testid={`variable-${v.key}`}
                >
                  {`{${v.key}}`}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Templates tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-[#F5F3EF] p-1 rounded-xl">
            {Object.entries(TEMPLATE_INFO).map(([key, info]) => {
              const Icon = info.icon;
              return (
                <TabsTrigger 
                  key={key} 
                  value={key}
                  className="data-[state=active]:bg-white data-[state=active]:shadow rounded-lg px-4 py-2"
                  data-testid={`tab-${key}`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {info.name}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {Object.entries(TEMPLATE_INFO).map(([templateType, info]) => {
            const template = templates[templateType];
            if (!template) return null;

            return (
              <TabsContent key={templateType} value={templateType}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Editor */}
                  <Card className="border-2 border-[#E5E0D8] rounded-2xl">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge className={info.color}>{info.name}</Badge>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPreviewMode(!previewMode)}
                          className="lg:hidden"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {previewMode ? 'Éditer' : 'Aperçu'}
                        </Button>
                      </div>
                      <CardDescription>{info.description}</CardDescription>
                    </CardHeader>
                    <CardContent className={`space-y-5 ${previewMode ? 'lg:block hidden' : ''}`}>
                      {/* Subject */}
                      <div className="space-y-2">
                        <Label className="text-[#2D2A26] font-medium">
                          Objet de l'email
                        </Label>
                        <Input
                          value={template.subject}
                          onChange={(e) => handleChange(templateType, 'subject', e.target.value)}
                          placeholder="Objet..."
                          className="h-11 border-2 border-[#E5E0D8] rounded-xl"
                          data-testid={`input-subject-${templateType}`}
                        />
                      </div>

                      {/* Title */}
                      <div className="space-y-2">
                        <Label className="text-[#2D2A26] font-medium">
                          Titre affiché
                        </Label>
                        <Input
                          value={template.title}
                          onChange={(e) => handleChange(templateType, 'title', e.target.value)}
                          placeholder="Titre..."
                          className="h-11 border-2 border-[#E5E0D8] rounded-xl"
                          data-testid={`input-title-${templateType}`}
                        />
                      </div>

                      {/* Body */}
                      <div className="space-y-2">
                        <Label className="text-[#2D2A26] font-medium">
                          Contenu principal
                        </Label>
                        <Textarea
                          name="body"
                          value={template.body}
                          onChange={(e) => handleChange(templateType, 'body', e.target.value)}
                          placeholder="Contenu de l'email..."
                          rows={10}
                          className="border-2 border-[#E5E0D8] rounded-xl resize-none font-mono text-sm"
                          data-testid={`input-body-${templateType}`}
                        />
                      </div>

                      {/* Footer */}
                      <div className="space-y-2">
                        <Label className="text-[#2D2A26] font-medium">
                          Pied de page
                        </Label>
                        <Textarea
                          name="footer"
                          value={template.footer}
                          onChange={(e) => handleChange(templateType, 'footer', e.target.value)}
                          placeholder="Pied de page..."
                          rows={2}
                          className="border-2 border-[#E5E0D8] rounded-xl resize-none text-sm"
                          data-testid={`input-footer-${templateType}`}
                        />
                      </div>

                      {/* Actions */}
                      <div className="flex gap-3 pt-4">
                        <Button
                          onClick={() => handleSave(templateType)}
                          disabled={saving}
                          className="btn-primary flex-1"
                          data-testid={`save-btn-${templateType}`}
                        >
                          {saving ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Sauvegarder
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleReset(templateType)}
                          disabled={saving}
                          className="border-2 border-[#E5E0D8] rounded-xl"
                          data-testid={`reset-btn-${templateType}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Réinitialiser
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Preview */}
                  <div className={`${previewMode ? '' : 'hidden lg:block'}`}>
                    <Card className="border-2 border-[#E5E0D8] rounded-2xl sticky top-24">
                      <CardHeader>
                        <CardTitle className="font-heading text-base text-[#2D2A26] flex items-center gap-2">
                          <Eye className="w-4 h-4 text-[#48b4ff]" />
                          Aperçu
                        </CardTitle>
                        <CardDescription>
                          Exemple avec des données fictives
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-sm mb-3 p-2 bg-[#F5F3EF] rounded-lg">
                          <strong>Objet :</strong> {template.subject
                            .replace('{nom_evenement}', 'Soirée jeux de société')
                            .replace('{nom_participant}', 'Jean Dupont')}
                        </div>
                        {renderPreview(template)}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </AdminLayout>
  );
}
